import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from "docx";
import { saveAs } from "file-saver";

export async function exportToPDF(elementId, filename = "resume.pdf") {
  const el = document.getElementById(elementId);
  if (!el) throw new Error("Preview element not found");
  // Render at full scale for crisp output
  const canvas = await html2canvas(el, {
    scale: 2,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
  });
  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * pageWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = 0;
  pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;
  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }
  pdf.save(filename);
}

function p(text, opts = {}) {
  return new Paragraph({
    children: [new TextRun({ text: text || "", bold: opts.bold, size: opts.size, color: opts.color })],
    spacing: { after: opts.after ?? 80 },
    alignment: opts.alignment,
  });
}

function heading(text) {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    children: [new TextRun({ text, bold: true, size: 26, color: "1E293B" })],
    spacing: { before: 200, after: 100 },
  });
}

export async function exportToDOCX(resume, filename = "resume.docx") {
  const pi = resume.personalInfo || {};
  const children = [];
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: pi.fullName || "Your Name", bold: true, size: 40 })],
      spacing: { after: 80 },
    })
  );
  const contactLine = [pi.email, pi.phone, pi.location, pi.website, pi.linkedin].filter(Boolean).join("  •  ");
  if (contactLine) {
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [new TextRun({ text: contactLine, size: 20, color: "475569" })],
        spacing: { after: 200 },
      })
    );
  }
  if (resume.summary) {
    children.push(heading("Professional Summary"));
    children.push(p(resume.summary, { size: 22 }));
  }
  if (resume.experience && resume.experience.length) {
    children.push(heading("Experience"));
    resume.experience.forEach((e) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `${e.role || ""}`, bold: true, size: 24 }),
            new TextRun({ text: e.company ? `  •  ${e.company}` : "", size: 22 }),
            new TextRun({ text: e.location ? `  •  ${e.location}` : "", size: 22, color: "475569" }),
          ],
        })
      );
      const dates = `${e.startDate || ""}${e.endDate ? ` — ${e.endDate}` : (e.current ? " — Present" : "")}`;
      if (dates.trim()) children.push(p(dates, { size: 20, color: "64748B", after: 60 }));
      (e.bullets || []).forEach((b) => {
        if (b) children.push(new Paragraph({ text: `• ${b}`, spacing: { after: 40 } }));
      });
    });
  }
  if (resume.education && resume.education.length) {
    children.push(heading("Education"));
    resume.education.forEach((ed) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: ed.degree || "", bold: true, size: 24 }),
            new TextRun({ text: ed.school ? `  •  ${ed.school}` : "", size: 22 }),
          ],
        })
      );
      const dates = `${ed.startDate || ""}${ed.endDate ? ` — ${ed.endDate}` : ""}`;
      if (dates.trim()) children.push(p(dates, { size: 20, color: "64748B", after: 80 }));
      if (ed.details) children.push(p(ed.details, { size: 22 }));
    });
  }
  if (resume.skills && resume.skills.length) {
    children.push(heading("Skills"));
    children.push(p(resume.skills.join(" • "), { size: 22 }));
  }
  if (resume.projects && resume.projects.length) {
    children.push(heading("Projects"));
    resume.projects.forEach((pr) => {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: pr.name || "Project", bold: true, size: 24 })],
        })
      );
      if (pr.description) children.push(p(pr.description, { size: 22 }));
    });
  }
  if (resume.showReferences && resume.references && resume.references.length) {
    children.push(heading("References"));
    resume.references.forEach((r) => {
      const label = `${r.name || ""}${r.type === "personal" ? " (Personal)" : ""}`;
      children.push(
        new Paragraph({
          children: [new TextRun({ text: label, bold: true, size: 24 })],
        })
      );
      if (r.title) children.push(p(r.title, { size: 22 }));
      const contact = [r.email, r.phone].filter(Boolean).join(" • ");
      if (contact) children.push(p(contact, { size: 22, color: "475569" }));
      if (r.note) children.push(p(r.note, { size: 20, color: "64748B" }));
    });
  } else {
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [new TextRun({ text: "References available upon request.", italics: true, size: 20, color: "475569" })],
        spacing: { before: 240 },
      })
    );
  }
  const doc = new Document({ sections: [{ properties: {}, children }] });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, filename);
}

// ---------------------------------------------------------------------------
// REFERENCES-ONLY EXPORTS
// A separate sheet with just the user's contact info + reference list, used
// when the user opts to keep references off the main resume but still wants
// to share them on request.
// ---------------------------------------------------------------------------
export async function exportReferencesToPDF(resume, filename = "references.pdf") {
  const pi = resume.personalInfo || {};
  const refs = resume.references || [];
  if (refs.length === 0) throw new Error("No references to export");
  const accent = resume.accentColor || "#0f172a";

  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 56;
  let y = margin;

  const accentRgb = hexToRgb(accent);

  // Title
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  pdf.setTextColor(accentRgb.r, accentRgb.g, accentRgb.b);
  pdf.text("References", margin, y);
  y += 24;

  // Owner name + contact
  pdf.setFontSize(13);
  pdf.setTextColor(15, 23, 42);
  if (pi.fullName) pdf.text(pi.fullName, margin, y);
  y += 16;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(71, 85, 105);
  const contactLine = [pi.email, pi.phone, pi.location].filter(Boolean).join("  •  ");
  if (contactLine) {
    pdf.text(contactLine, margin, y);
    y += 14;
  }

  // Accent rule
  pdf.setDrawColor(accentRgb.r, accentRgb.g, accentRgb.b);
  pdf.setLineWidth(1);
  pdf.line(margin, y + 6, pageW - margin, y + 6);
  y += 26;

  // References
  refs.forEach((r, i) => {
    if (y > pageH - 100) {
      pdf.addPage();
      y = margin;
    }
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(15, 23, 42);
    const nameLine = `${i + 1}. ${r.name || ""}${r.type === "personal" ? "  (Personal)" : ""}`;
    pdf.text(nameLine, margin, y);
    y += 14;

    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(10);
    pdf.setTextColor(71, 85, 105);
    if (r.title) {
      pdf.text(r.title, margin, y);
      y += 13;
    }

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(51, 65, 85);
    const contact = [r.email, r.phone].filter(Boolean).join("    •    ");
    if (contact) {
      pdf.text(contact, margin, y);
      y += 13;
    }

    if (r.note) {
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(9.5);
      pdf.setTextColor(100, 116, 139);
      const wrapped = pdf.splitTextToSize(r.note, pageW - margin * 2);
      pdf.text(wrapped, margin, y);
      y += wrapped.length * 12;
    }

    y += 14;
  });

  pdf.save(filename);
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

export async function exportReferencesToDOCX(resume, filename = "references.docx") {
  const pi = resume.personalInfo || {};
  const refs = resume.references || [];
  if (refs.length === 0) throw new Error("No references to export");
  const children = [];

  children.push(
    new Paragraph({
      alignment: AlignmentType.LEFT,
      children: [new TextRun({ text: "References", bold: true, size: 40, color: "0F172A" })],
      spacing: { after: 120 },
    })
  );
  if (pi.fullName) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: pi.fullName, bold: true, size: 26 })],
        spacing: { after: 60 },
      })
    );
  }
  const contactLine = [pi.email, pi.phone, pi.location].filter(Boolean).join("  •  ");
  if (contactLine) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: contactLine, size: 20, color: "475569" })],
        spacing: { after: 240 },
      })
    );
  }

  refs.forEach((r, i) => {
    const nameLine = `${i + 1}. ${r.name || ""}${r.type === "personal" ? "  (Personal)" : ""}`;
    children.push(
      new Paragraph({
        children: [new TextRun({ text: nameLine, bold: true, size: 24 })],
        spacing: { after: 40 },
      })
    );
    if (r.title) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: r.title, italics: true, size: 22, color: "475569" })],
          spacing: { after: 40 },
        })
      );
    }
    const contact = [r.email, r.phone].filter(Boolean).join("  •  ");
    if (contact) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: contact, size: 22 })],
          spacing: { after: 40 },
        })
      );
    }
    if (r.note) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: r.note, italics: true, size: 20, color: "64748B" })],
          spacing: { after: 200 },
        })
      );
    } else {
      children.push(new Paragraph({ children: [new TextRun({ text: "", size: 20 })], spacing: { after: 160 } }));
    }
  });

  const doc = new Document({ sections: [{ properties: {}, children }] });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, filename);
}
